import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Unterzeichner } from '../shared/constants';
import { Vorgang } from '../types';
import { Signature } from './common/Signature';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function AuftragZurErstellungFahrzeugbewertung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.auftragZurErstellungFahrzeugbewertung}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ auftragZurErstellungFahrzeugbewertung: event.target.checked })}
              name="auftragZurErstellungFahrzeugbewertung"
              color="primary"
              data-testid="auftragZurErstellungFahrzeugbewertungCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="auftragZurErstellungFahrzeugbewertungLink"
              >
                Auftrag zur Erstellung einer Fahrzeugbewertung
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.*
            </>
          }
        />
      </Grid>
      {isModalOpen && <AuftragModal vorgang={vorgang} isLoading={isLoading} isModalOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function AuftragModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const url = useDateiUrl(vorgang?.unterschriften?.auftragZurErstellungFahrzeugbewertung?.key);

  const signDate = vorgang?.unterschriften?.auftragZurErstellungFahrzeugbewertung?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Auftrag zur Erstellung einer Fahrzeugbewertung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="auftragModal">
          <Grid size={12}>
            <Grid container>
              <Grid size={4}>
                Zur Fahrzeugbewertung
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="vorgangsnummer">
                {vorgang.vorgangsnummer}
              </Grid>
              <Grid size={4}>
                Name des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftraggeber">
                {vorgang.auftraggeber?.anrede} {vorgang.auftraggeber?.vorname} {vorgang.auftraggeber?.nachname}
              </Grid>
              <Grid size={4}>
                Anschrift des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="anschriftAuftraggeber">
                {vorgang?.auftraggeber?.strasse} {vorgang?.auftraggeber?.plz} {vorgang?.auftraggeber?.ort}
              </Grid>
              <Grid size={4}>
                Kennzeichen des Auftraggebers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="kennzeichenAuftraggeber">
                {vorgang.fahrzeugdaten.kennzeichen}
              </Grid>
              <Grid size={4}>
                Name des Auftragnehmers
              </Grid>
              <Grid size={1}>
                :
              </Grid>
              <Grid size={7} data-testid="nameAuftragnehmer">
                Experts24 Tassone GmbH, Hildener Str. 28, 42697 Solingen
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§1 Geltung der Bedingungen für eine Fahrzeugbewertung</Typography>
            <Typography variant="body2">
              Die Erstellung der Fahrzeugbewertung durch den Auftragnehmer (AN) für den Auftraggeber (AG) erfolgt ausschließlich aufgrund dieser
              Geschäftsbedingungen.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§2 Auftragserteilung</Typography>
            <Typography variant="body2">
              Der Auftrag zur Gutachtenerstellung erfolgt regelmäßig durch (auch digitale) Unterschrift des AG unter dem Auftragsformular und Zugang des
              unterschriebenen Auftragsformulars beim AN.
            </Typography>
            <Typography variant="body2">
              Der Auftrag kann alternativ auch im Wege der elektronischen Auftragserteilung zustande kommen. In diesem Fall gibt der AG ein verbindliches
              Angebot ab, indem er
            </Typography>
            <Typography variant="body2">
              - die im Beauftragungsprozess bereitgestellten Vertragsbedingungen durch Anklicken der hierfür vorgesehenen Check-Boxen akzeptiert und
            </Typography>
            <Typography variant="body2">
              - den Auftrag durch Betätigung des eindeutig beschrifteten Buttons „kostenpflichtig bestellen“ bzw. „kostenpflichtig beauftragen“ absendet.
            </Typography>
            <Typography variant="body2">
              Die Auftragsannahme erfolgt durch Erklärung des AN gegenüber dem AG, insbesondere durch elektronische Bestätigung der Auftragsannahme.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§3 Erstellung der Fahrzeugbewertung, Übersendung und Ausfertigungen</Typography>
            <Typography variant="body2">
              (1) Der AN erstellt zum o.g. Fahrzeug eine Fahrzeugbewertung für den AG. Der AN ist verpflichtet, den erteilten Auftrag nach bestem Wissen und
              Gewissen auszuführen. <br />
              (2) Der AG erhält die Fahrzeugbewertung im Original mit Lichtbildsatz postalisch.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§4 Vergütung</Typography>
            <Typography variant="body2">
              Sofern keine abweichende Vereinbarung mit dem Kunden getroffen wurde, gelten folgende Standardkosten des Auftragnehmers (AN):
              <ul>
                <li>Fahrzeugbewertung: 125,21 € netto (149,00 € brutto)</li>
                <li>Fahrzeugbewertung E-Fahrzeug: 251,26 € netto (299,00 € brutto)</li>
              </ul>
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={url}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.auftragZurErstellungFahrzeugbewertung?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
